import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { 
  Clock, CheckCircle, ChefHat, Package, X, RefreshCw, Bell, BellOff, Download
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useOrderNotifications, requestNotificationPermission, getNotificationStatus } from '../hooks/useOrderNotifications';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const translations = {
  es: {
    title: 'Mis Pedidos',
    empty: 'No tienes pedidos activos',
    emptyHint: 'Añade productos y envía tu pedido',
    total: 'Total de la mesa',
    status: {
      pendiente: 'Recibido',
      preparando: 'Preparando',
      listo: 'Listo para servir',
      entregado: 'Entregado'
    },
    orderNumber: 'Pedido',
    close: 'Cerrar',
    notifications: 'Notificaciones',
    notificationsOn: 'Recibirás alertas cuando tu pedido cambie de estado',
    notificationsOff: 'Activa las notificaciones para recibir alertas',
    enableNotifications: 'Activar'
  },
  en: {
    title: 'My Orders',
    empty: 'You have no active orders',
    emptyHint: 'Add products and send your order',
    total: 'Table total',
    status: {
      pendiente: 'Received',
      preparando: 'Preparing',
      listo: 'Ready to serve',
      entregado: 'Delivered'
    },
    orderNumber: 'Order',
    close: 'Close',
    notifications: 'Notifications',
    notificationsOn: 'You will receive alerts when your order status changes',
    notificationsOff: 'Enable notifications to receive alerts',
    enableNotifications: 'Enable'
  },
  cat: {
    title: 'Les meves comandes',
    empty: 'No tens comandes actives',
    emptyHint: 'Afegeix productes i envia la teva comanda',
    total: 'Total de la taula',
    status: {
      pendiente: 'Rebut',
      preparando: 'Preparant',
      listo: 'Llest per servir',
      entregado: 'Entregat'
    },
    orderNumber: 'Comanda',
    close: 'Tancar',
    notifications: 'Notificacions',
    notificationsOn: 'Rebràs alertes quan la teva comanda canviï d\'estat',
    notificationsOff: 'Activa les notificacions per rebre alertes',
    enableNotifications: 'Activar'
  },
  fr: {
    title: 'Mes commandes',
    empty: 'Vous n\'avez pas de commandes actives',
    emptyHint: 'Ajoutez des produits et envoyez votre commande',
    total: 'Total de la table',
    status: {
      pendiente: 'Reçue',
      preparando: 'En préparation',
      listo: 'Prête à servir',
      entregado: 'Livrée'
    },
    orderNumber: 'Commande',
    close: 'Fermer',
    notifications: 'Notifications',
    notificationsOn: 'Vous recevrez des alertes quand le statut change',
    notificationsOff: 'Activez les notifications pour recevoir des alertes',
    enableNotifications: 'Activer'
  }
};

const getStatusIcon = (estado) => {
  switch (estado) {
    case 'pendiente': return <Clock className="text-amber-500" size={18} />;
    case 'preparando': return <ChefHat className="text-orange-500 animate-pulse" size={18} />;
    case 'listo': return <Package className="text-[#B78C3C]" size={18} />;
    case 'entregado': return <CheckCircle className="text-gray-400" size={18} />;
    default: return <Clock size={18} />;
  }
};

const getStatusColor = (estado) => {
  switch (estado) {
    case 'pendiente': return 'bg-amber-100 text-amber-700 border-amber-300';
    case 'preparando': return 'bg-orange-100 text-orange-700 border-orange-300';
    case 'listo': return 'bg-amber-100 text-amber-700 border-amber-300';
    case 'entregado': return 'bg-gray-100 text-gray-500 border-gray-300';
    default: return 'bg-gray-100 text-gray-700';
  }
};

export const CustomerOrdersPanel = ({ 
  mesaNumero, 
  isOpen, 
  onClose, 
  language = 'es' 
}) => {
  const { isDark } = useTheme();
  const [orders, setOrders] = useState([]);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState('default');
  const isFirstFetch = useRef(true);
  const t = translations[language] || translations.es;
  
  const { checkAndNotify, initializeOrders } = useOrderNotifications(mesaNumero, language);

  // Check notification permission on mount
  useEffect(() => {
    const status = getNotificationStatus();
    setNotificationStatus(status);
    setNotificationsEnabled(status === 'granted');
  }, []);

  const handleEnableNotifications = async () => {
    // Check notification status
    const status = getNotificationStatus();
    
    // Not supported
    if (status === 'unsupported') {
      toast.error(
        language === 'es' 
          ? 'Tu navegador no soporta notificaciones. Prueba con Chrome o añade la página a tu pantalla de inicio.' 
          : language === 'en'
          ? 'Your browser does not support notifications. Try Chrome or add the page to your home screen.'
          : 'El teu navegador no suporta notificacions.'
      );
      return;
    }

    // Already denied
    if (status === 'denied') {
      toast.error(
        language === 'es' 
          ? 'Las notificaciones están bloqueadas. Ve a Ajustes > Sitio web > Notificaciones para activarlas.' 
          : language === 'en'
          ? 'Notifications are blocked. Go to Settings > Website > Notifications to enable them.'
          : 'Les notificacions estan bloquejades.'
      , { duration: 5000 });
      return;
    }

    // Already granted
    if (status === 'granted') {
      toast.success(
        language === 'es' 
          ? '¡Las notificaciones ya están activadas!' 
          : language === 'en'
          ? 'Notifications are already enabled!'
          : 'Les notificacions ja estan activades!'
      );
      return;
    }

    // Request permission
    try {
      const granted = await requestNotificationPermission();
      setNotificationsEnabled(granted);
      setNotificationStatus(granted ? 'granted' : 'denied');
      
      if (granted) {
        toast.success(
          language === 'es' 
            ? '¡Notificaciones activadas! Te avisaremos cuando tu pedido cambie.' 
            : language === 'en'
            ? 'Notifications enabled! We\'ll notify you when your order status changes.'
            : 'Notificacions activades!'
        );
      } else {
        toast.error(
          language === 'es' 
            ? 'No se pudieron activar las notificaciones' 
            : language === 'en'
            ? 'Could not enable notifications'
            : 'No s\'han pogut activar les notificacions'
        );
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error(
        language === 'es' 
          ? 'Error al activar notificaciones' 
          : language === 'en'
          ? 'Error enabling notifications'
          : 'Error en activar notificacions'
      );
    }
  };

  const fetchOrders = useCallback(async () => {
    if (!mesaNumero) return;
    
    try {
      const response = await fetch(`${API_URL}/api/mesa/${mesaNumero}/session`);
      if (response.ok) {
        const data = await response.json();
        
        if (data.session) {
          setSession(data.session);
          const newOrders = data.orders || [];
          
          // First fetch: just initialize without notifying
          if (isFirstFetch.current) {
            initializeOrders(newOrders);
            isFirstFetch.current = false;
          } else if (!data.session.is_closed) {
            // Only check for notifications on active sessions
            checkAndNotify(newOrders);
          }
          
          setOrders(newOrders);
        } else {
          setSession(null);
          setOrders([]);
        }
      } else {
        setSession(null);
        setOrders([]);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [mesaNumero, checkAndNotify, initializeOrders]);

  useEffect(() => {
    if (isOpen && mesaNumero) {
      fetchOrders();
      // Poll every 10 seconds for status updates
      const interval = setInterval(fetchOrders, 10000);
      return () => clearInterval(interval);
    }
  }, [isOpen, mesaNumero, fetchOrders]);

  if (!isOpen) return null;

  const getTimeAgo = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return language === 'es' ? 'Ahora' : language === 'en' ? 'Now' : 'Ara';
    if (diffMins < 60) return `${diffMins} min`;
    return `${Math.floor(diffMins / 60)}h ${diffMins % 60}m`;
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" />
      
      {/* Panel - Centered modal */}
      <div 
        className={`relative w-full max-w-md max-h-[75vh] rounded-2xl overflow-hidden shadow-2xl transition-colors duration-300 ${
          isDark ? 'bg-stone-900' : 'bg-white'
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className={`sticky top-0 z-10 px-4 py-4 flex items-center justify-between border-b ${
            isDark ? 'bg-stone-900 border-stone-700' : 'bg-white border-stone-200'
          }`}
        >
          <div>
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-stone-800'}`}>
              {t.title}
            </h2>
            {session && (
              <p className={`text-sm ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                Mesa #{mesaNumero}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Notification toggle */}
            <button
              onClick={handleEnableNotifications}
              className={`p-2 rounded-full transition-colors ${
                notificationsEnabled 
                  ? (isDark ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-100 text-amber-600')
                  : (isDark ? 'hover:bg-stone-800 text-amber-400' : 'hover:bg-amber-50 text-amber-500')
              }`}
              title={notificationsEnabled ? t.notificationsOn : t.notificationsOff}
              data-testid="notification-toggle"
            >
              {notificationsEnabled ? <Bell size={18} /> : <BellOff size={18} />}
            </button>
            <button
              onClick={fetchOrders}
              className={`p-2 rounded-full ${isDark ? 'hover:bg-stone-800' : 'hover:bg-stone-100'}`}
            >
              <RefreshCw size={18} className={isDark ? 'text-stone-400' : 'text-stone-500'} />
            </button>
            <button
              onClick={onClose}
              className={`p-2 rounded-full ${isDark ? 'hover:bg-stone-800' : 'hover:bg-stone-100'}`}
            >
              <X size={20} className={isDark ? 'text-stone-400' : 'text-stone-500'} />
            </button>
          </div>
        </div>

        {/* Notification prompt banner */}
        {!notificationsEnabled && orders.length > 0 && notificationStatus !== 'unsupported' && (
          <div 
            className={`px-4 py-3 flex items-center justify-between ${
              notificationStatus === 'denied'
                ? (isDark ? 'bg-red-900/30 border-b border-red-800' : 'bg-red-50 border-b border-red-200')
                : (isDark ? 'bg-amber-900/30 border-b border-amber-800' : 'bg-amber-50 border-b border-amber-200')
            }`}
          >
            <div className="flex items-center gap-2">
              <Bell size={16} className={
                notificationStatus === 'denied'
                  ? (isDark ? 'text-red-400' : 'text-red-600')
                  : (isDark ? 'text-amber-400' : 'text-amber-600')
              } />
              <span className={`text-sm ${
                notificationStatus === 'denied'
                  ? (isDark ? 'text-red-200' : 'text-red-800')
                  : (isDark ? 'text-amber-200' : 'text-amber-800')
              }`}>
                {notificationStatus === 'denied' 
                  ? (language === 'es' ? 'Notificaciones bloqueadas' : 'Notifications blocked')
                  : t.notificationsOff
                }
              </span>
            </div>
            <button
              onClick={handleEnableNotifications}
              className={`text-sm font-medium px-3 py-1 rounded-full ${
                notificationStatus === 'denied'
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : (isDark 
                    ? 'bg-amber-600 text-white hover:bg-amber-500' 
                    : 'bg-amber-600 text-white hover:bg-amber-700')
              }`}
              data-testid="enable-notifications-btn"
            >
              {notificationStatus === 'denied' 
                ? (language === 'es' ? 'Ver cómo' : 'How to fix')
                : t.enableNotifications
              }
            </button>
          </div>
        )}

        {/* Closed session banner */}
        {session?.is_closed && (
          <div className={`px-4 py-3 ${isDark ? 'bg-blue-900/30 border-b border-blue-800' : 'bg-blue-50 border-b border-blue-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle size={18} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
                <span className={`text-sm font-medium ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
                  {language === 'es' ? 'Mesa cerrada' : language === 'en' ? 'Table closed' : language === 'fr' ? 'Table fermée' : 'Taula tancada'}
                </span>
              </div>
              <span className={`text-xs ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                {language === 'es' ? '¡Gracias por tu visita!' : language === 'en' ? 'Thanks for visiting!' : language === 'fr' ? 'Merci de votre visite !' : 'Gràcies per la visita!'}
              </span>
            </div>
          </div>
        )}

        {/* READY ORDERS ALERT - Prominent visual notification */}
        {orders.some(o => o.estado === 'listo') && (
          <div 
            className="px-4 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white animate-pulse"
            data-testid="ready-orders-alert"
          >
            <div className="flex items-center justify-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <CheckCircle size={24} className="text-white" />
              </div>
              <div className="text-center">
                <p className="font-bold text-lg">
                  {language === 'es' ? '¡Pedido listo!' : 
                   language === 'en' ? 'Order ready!' : 
                   language === 'fr' ? 'Commande prête !' : 
                   'Comanda llesta!'}
                </p>
                <p className="text-sm text-white/90">
                  {language === 'es' ? 'Tu pedido está listo para servir' : 
                   language === 'en' ? 'Your order is ready to be served' : 
                   language === 'fr' ? 'Votre commande est prête à être servie' : 
                   'La teva comanda està llesta per servir'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="overflow-y-auto max-h-[50vh] p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="animate-spin text-stone-400" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-12">
              <Package size={48} className={`mx-auto mb-4 ${isDark ? 'text-stone-600' : 'text-stone-300'}`} />
              <p className={`text-lg font-medium ${isDark ? 'text-stone-300' : 'text-stone-600'}`}>
                {t.empty}
              </p>
              <p className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>
                {t.emptyHint}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order, idx) => (
                <div
                  key={order.id}
                  className={`rounded-xl p-4 border transition-colors ${
                    isDark 
                      ? 'bg-stone-800 border-stone-700' 
                      : 'bg-stone-50 border-stone-200'
                  }`}
                >
                  {/* Order header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${isDark ? 'text-stone-200' : 'text-stone-700'}`}>
                        {t.orderNumber} #{idx + 1}
                      </span>
                      <span className={`text-sm ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>
                        {getTimeAgo(order.created_at)}
                      </span>
                    </div>
                    <Badge className={`flex items-center gap-1 border ${getStatusColor(order.estado)}`}>
                      {getStatusIcon(order.estado)}
                      <span className="text-xs font-medium">{t.status[order.estado]}</span>
                    </Badge>
                  </div>

                  {/* Order items */}
                  <ul className="space-y-2">
                    {order.items.map((item, itemIdx) => (
                      <li 
                        key={itemIdx}
                        className={`flex justify-between text-sm ${
                          isDark ? 'text-stone-300' : 'text-stone-600'
                        }`}
                      >
                        <span>
                          {item.quantity}x {item.product_name}
                          {item.options?.length > 0 && (
                            <span className={`ml-1 ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>
                              ({item.options.join(', ')})
                            </span>
                          )}
                        </span>
                        <span className="font-medium">{item.subtotal.toFixed(2)}€</span>
                      </li>
                    ))}
                  </ul>

                  {/* Order total */}
                  <div className={`mt-3 pt-3 border-t flex justify-between ${
                    isDark ? 'border-stone-700' : 'border-stone-200'
                  }`}>
                    <span className={`font-medium ${isDark ? 'text-stone-300' : 'text-stone-600'}`}>
                      Total
                    </span>
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-stone-800'}`}>
                      {order.total.toFixed(2)}€
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with session total and download button */}
        {session && orders.length > 0 && (
          <div 
            className={`p-4 border-t ${
              session.is_closed 
                ? (isDark ? 'bg-amber-900/20 border-amber-800' : 'bg-amber-50 border-amber-200')
                : (isDark ? 'bg-stone-800 border-stone-700' : 'bg-stone-100 border-stone-200')
            }`}
          >
            <div className="flex justify-between items-center mb-3">
              <span className={`text-lg font-medium ${isDark ? 'text-stone-300' : 'text-stone-600'}`}>
                {t.total}
              </span>
              <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-stone-800'}`}>
                {session.total_amount?.toFixed(2) || '0.00'}€
              </span>
            </div>
            
            {/* Download ticket button - more prominent when closed */}
            <Button
              variant={session.is_closed ? "default" : "outline"}
              size="sm"
              className={`w-full flex items-center justify-center gap-2 ${
                session.is_closed 
                  ? 'bg-[#B78C3C] hover:bg-[#9E7530] text-white'
                  : (isDark ? 'border-stone-600 text-stone-300 hover:bg-stone-700' : '')
              }`}
              onClick={() => {
                const link = document.createElement('a');
                link.href = `${API_URL}/api/mesa/${mesaNumero}/ticket`;
                link.download = `ticket_mesa_${mesaNumero}.pdf`;
                link.click();
                toast.success(
                  language === 'es' 
                    ? 'Descargando ticket...' 
                    : language === 'en'
                    ? 'Downloading receipt...'
                    : language === 'fr'
                    ? 'Téléchargement du ticket...'
                    : 'Descarregant tiquet...'
                );
              }}
            >
              <Download size={16} />
              {session.is_closed 
                ? (language === 'es' ? 'Descargar tu ticket' : language === 'en' ? 'Download your receipt' : language === 'fr' ? 'Télécharger votre ticket' : 'Descarrega el tiquet')
                : (language === 'es' ? 'Descargar ticket' : language === 'en' ? 'Download receipt' : language === 'fr' ? 'Télécharger ticket' : 'Descarregar tiquet')
              }
            </Button>
            
            {session.is_closed && (
              <p className={`text-xs text-center mt-2 ${isDark ? 'text-stone-400' : 'text-stone-500'}`}>
                {language === 'es' ? 'Puedes descargar tu ticket en cualquier momento' : language === 'en' ? 'You can download your receipt anytime' : language === 'fr' ? 'Vous pouvez télécharger votre ticket à tout moment' : 'Pots descarregar el tiquet en qualsevol moment'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerOrdersPanel;
